class KannakiPredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.form = this.querySelector('form');
    this.input = this.querySelector('input[type="search"]');
    this.resultsElement = this.querySelector('.predictive-search__results');
    
    if (!this.input || !this.resultsElement) return;

    this.setupEventListeners();
  }

  setupEventListeners() {
    // Enable form submission on enter key or submit button click
    if (this.form) {
      this.form.addEventListener('submit', (e) => {
        const searchTerm = this.input.value.trim();
        if (searchTerm.length > 0) {
          const query = searchTerm.endsWith('*') ? searchTerm : `${searchTerm}*`;
          window.location.href = `${this.form.action}?q=${encodeURIComponent(query)}&options[prefix]=last`;
        }
        e.preventDefault();
      });
    }

    let debounceTimer;
    this.input.addEventListener('input', () => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        this.onChange();
      }, 200);
    });

    document.addEventListener('click', (e) => {
      if (!this.contains(e.target)) {
        this.close();
      }
    });

    this.input.addEventListener('focus', () => {
      if (this.input.value.trim().length > 0) {
        this.onChange();
      }
    });

    // Close on Escape key
    this.input.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.close();
      }
    });
  }

  onChange() {
    const searchTerm = this.input.value.trim();

    if (!searchTerm.length) {
      this.close();
      return;
    }

    this.getSearchResults(searchTerm);
  }

  getSearchResults(searchTerm) {
    const query = searchTerm.endsWith('*') ? searchTerm : `${searchTerm}*`;
    fetch(`/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=6&resources[options][prefix]=last`)
      .then((response) => {
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        return response.json();
      })
      .then((data) => {
        let products = data.resources && data.resources.results ? data.resources.results.products : [];
        if (!products || products.length === 0) {
          // Fallback fetch without wildcard if needed
          return fetch(`/search/suggest.json?q=${encodeURIComponent(searchTerm)}&resources[type]=product&resources[limit]=6`)
            .then(res => res.json())
            .then(dataFallback => {
              const fallbackProducts = dataFallback.resources && dataFallback.resources.results ? dataFallback.resources.results.products : [];
              this.renderResults(fallbackProducts, searchTerm);
            });
        }
        this.renderResults(products, searchTerm);
      })
      .catch((error) => {
        console.error('Error fetching predictive search results:', error);
        this.close();
      });
  }

  renderResults(products, searchTerm) {
    if (!products || products.length === 0) {
      this.resultsElement.innerHTML = `
        <div style="padding: 16px; text-align: center; color: #888; font-family: 'Montserrat', sans-serif; font-size: 0.82rem;">
          No matching products found for "${searchTerm}"
        </div>
      `;
      this.open();
      return;
    }

    let html = `<ul style="list-style: none; margin: 0; padding: 0; max-height: 340px; overflow-y: auto;">`;
    products.forEach((product) => {
      html += `
        <li style="border-bottom: 1px solid rgba(255,255,255,0.06); transition: background-color 0.2s ease;">
          <a href="${product.url}" style="display: flex; align-items: center; padding: 10px 14px; text-decoration: none; color: #fff; gap: 12px;">
            ${product.image ? `<img src="${product.image}" alt="${product.title}" style="width: 42px; height: 52px; object-fit: cover; border-radius: 4px; flex-shrink: 0; border: 1px solid rgba(212, 175, 55, 0.2);">` : ''}
            <div style="flex: 1; min-width: 0;">
              <div style="font-family: 'Montserrat', sans-serif; font-size: 0.82rem; font-weight: 500; color: #FFFFFF; text-overflow: ellipsis; overflow: hidden; white-space: nowrap; margin-bottom: 2px;">${product.title}</div>
              <div style="color: #D4AF37; font-size: 0.78rem; font-weight: 600; font-family: 'Montserrat', sans-serif;">${product.price}</div>
            </div>
          </a>
        </li>
      `;
    });
    html += `</ul>
      <div style="padding: 10px 14px; text-align: center; background: rgba(5,5,5,0.95); border-top: 1px solid rgba(212, 175, 55, 0.3);">
        <a href="/search?q=${encodeURIComponent(searchTerm.endsWith('*') ? searchTerm : searchTerm + '*')}&options[prefix]=last" style="display: inline-block; color: #D4AF37; text-decoration: none; text-transform: uppercase; font-size: 0.75rem; letter-spacing: 0.12em; font-weight: 600; font-family: 'Montserrat', sans-serif; transition: opacity 0.2s ease;">
          View all results for "${searchTerm}" &rarr;
        </a>
      </div>
    `;

    this.resultsElement.innerHTML = html;
    this.open();
  }

  open() {
    this.resultsElement.style.display = 'block';
    this.setAttribute('open', 'true');
  }

  close() {
    this.resultsElement.style.display = 'none';
    this.removeAttribute('open');
  }
}

if (!customElements.get('kannaki-predictive-search')) {
  customElements.define('kannaki-predictive-search', KannakiPredictiveSearch);
}
